import { useCallback, useEffect, useState } from 'react';
import { GetObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import awsExports from '../aws-exports';
import {
  MeshAlgorithmResultsForElement,
  MeshAlgorithmResultsMap,
} from '../domain/property-operations';
import { useUserTenant } from '../services/auth-info';
import { useActiveModels } from './active-models';
import { useS3Client } from './s3';

type MeshAlgorithmResultsFileData = Record<
  string,
  MeshAlgorithmResultsForElement
>;

/**
 * Hook to load precomputed mesh algorithm results for all active models in a project.
 *
 * The result files are stored in S3 at:
 * {tenant}/project/{projectId}/bimModels/{version}/{filename}_geometry.json
 *
 * This hook fetches these files and returns them mapped by model URN.
 */
export function useMeshAlgorithmResults(projectId: string) {
  const { tenant, tenantTier } = useUserTenant();
  const { activeModels, fetching: fetchingModels } = useActiveModels(projectId);
  const s3Client = useS3Client(projectId);

  const [meshAlgorithmResults, setMeshAlgorithmResults] =
    useState<MeshAlgorithmResultsMap>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Only fetch for pro tier users (results are only computed for pro)
  // tenantTier can be 'PRO', 'PAID', 'FREE' (uppercase enum values)
  const isProTier = tenantTier?.toLowerCase() === 'pro' || tenantTier === 'PRO';

  const fetchResultsForModel = useCallback(
    async (
      tenantGroup: string,
      modelVersion: number,
      modelFilename: string,
      modelUrn: string
    ): Promise<[string, MeshAlgorithmResultsFileData | null]> => {
      const bucketName = awsExports.models_bucket_name;
      if (!bucketName) {
        console.warn(
          '[MeshAlgorithmResults] models_bucket_name not configured'
        );
        return [modelUrn, null];
      }

      // Construct S3 key: {tenant}/project/{projectId}/bimModels/{version}/{filename}_geometry.json
      const baseName = modelFilename.replace(/\.[^/.]+$/, '');
      const s3Key = `${tenantGroup}/project/${projectId}/bimModels/${modelVersion}/${baseName}_geometry.json`;

      console.log(
        `[MeshAlgorithmResults] Fetching from s3://${bucketName}/${s3Key}`
      );

      try {
        const command = new GetObjectCommand({
          Bucket: bucketName,
          Key: s3Key,
        });

        const signedUrl = await getSignedUrl(s3Client, command, {
          expiresIn: 3600,
        });

        const response = await fetch(signedUrl);

        if (!response.ok) {
          if (response.status === 404) {
            console.log(
              `[MeshAlgorithmResults] No results file found for model ${modelUrn} (this is normal if OBJ processing hasn't completed)`
            );
            return [modelUrn, null];
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data: MeshAlgorithmResultsFileData = await response.json();
        console.log(
          `[MeshAlgorithmResults] Loaded ${
            Object.keys(data).length
          } elements for model ${modelUrn}`
        );

        return [modelUrn, data];
      } catch (err) {
        // Don't log as error for AccessDenied (common when file doesn't exist)
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        if (
          errorMessage.includes('AccessDenied') ||
          errorMessage.includes('NoSuchKey')
        ) {
          console.log(
            `[MeshAlgorithmResults] Results file not available for model ${modelUrn}`
          );
        } else {
          console.warn(
            `[MeshAlgorithmResults] Failed to load results for model ${modelUrn}:`,
            err
          );
        }
        return [modelUrn, null];
      }
    },
    [projectId, s3Client]
  );

  useEffect(() => {
    // Skip if not pro tier or still fetching models
    if (!isProTier || fetchingModels || !activeModels || !tenant?.group) {
      return;
    }

    const loadAllResults = async () => {
      setLoading(true);
      setError(null);

      try {
        const results = await Promise.all(
          activeModels.map((model) =>
            fetchResultsForModel(
              tenant.group!,
              model.version,
              model.filename,
              model.forgeUrn || model.id
            )
          )
        );

        const resultsMap: MeshAlgorithmResultsMap = {};
        let totalLoaded = 0;

        for (const [modelUrn, data] of results) {
          if (data) {
            resultsMap[modelUrn] = data;
            totalLoaded++;
          }
        }

        if (totalLoaded > 0) {
          console.log(
            `[MeshAlgorithmResults] Successfully loaded results for ${totalLoaded}/${activeModels.length} models`
          );
        }

        setMeshAlgorithmResults(resultsMap);
      } catch (err) {
        console.error('[MeshAlgorithmResults] Error loading results:', err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
      } finally {
        setLoading(false);
      }
    };

    loadAllResults();
  }, [
    isProTier,
    fetchingModels,
    activeModels,
    tenant?.group,
    fetchResultsForModel,
  ]);

  return {
    meshAlgorithmResults: isProTier ? meshAlgorithmResults : undefined,
    loading,
    error,
    isProTier,
  };
}
